import type { EditableImageListRecord } from '../../types/index.ts';
import { closestCenter } from '@dnd-kit/collision';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import { useCallback } from 'react';
import { PiXBold } from 'react-icons/pi';
import { Spinner } from '@_/ui';
import { isTruthy } from '@_/utils';

export interface ImageListItemProps {
  containerRef?: React.RefObject<HTMLDivElement | null>;
  index: number;
  item: EditableImageListRecord;
  onRemove: (itemId: string) => void;
  renderImage: (props: { key?: string; imageUrl: string; index: number }) => React.ReactNode;
  renderPlaceholder: () => React.ReactNode;
}

export function ImageListItem({
  containerRef,
  index,
  onRemove,
  item,
  renderImage,
  renderPlaceholder
}: ImageListItemProps) {
  const sortable = useSortable({
    collisionDetector: closestCenter,
    id: item.id,
    index,
    modifiers: [
      containerRef ? RestrictToElement.configure({ element: containerRef.current }) : undefined
    ].filter(isTruthy)
  });

  const handleRemove = useCallback(() => onRemove(item.id), [onRemove, item.id]);

  if (sortable.isDragging) {
    return <div ref={sortable.ref}>{renderPlaceholder()}</div>;
  }

  return (
    <div className="group relative" ref={sortable.ref}>
      {renderImage({ imageUrl: item.imageUrl, index })}

      <div className="pointer-events-none absolute inset-0 z-0 hidden rounded-lg bg-black/30 group-hover:block" />

      {item.isPending && item.upload.inProgress && (
        <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center bg-white/75">
          <Spinner className="size-8" stroke={3} />
        </div>
      )}

      <button
        className="absolute top-0 right-0 -mt-1.5 -mr-1.5 block rounded-full bg-gray-800 p-1 text-white"
        onClick={handleRemove}
        type="button"
      >
        <PiXBold className="size-3" />
      </button>
    </div>
  );
}
